import { Auth } from 'firebase/auth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPasswordResetErrorKey } from '@/firebase/account-actions';
import {
  initiateAppleSignIn,
  initiateEmailSignIn,
  initiateEmailSignUp,
  initiateGoogleSignIn,
  initiatePasswordReset,
  getAuthErrorKey,
} from '@/firebase/non-blocking-login';
import { useToast } from '@/hooks/use-toast';

export type LoadingType = 'signIn' | 'signUp' | 'google' | 'apple' | 'forgot' | null;

export function useLoginForm(auth: Auth) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState<LoadingType>(null);

  const handleAuthError = (err: unknown) => {
    const errorKey = getAuthErrorKey(err);
    if (errorKey) {
      toast({
        title: t('common.error'),
        description: t(errorKey),
        variant: 'destructive',
      });
    }
  };

  const handleSignUp = async () => {
    setLoading('signUp');
    auth.languageCode = i18n.resolvedLanguage?.split('-')[0] ?? null;
    try {
      await initiateEmailSignUp(auth, email, password);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(null);
    }
  };

  const handleSignIn = async () => {
    setLoading('signIn');
    try {
      await initiateEmailSignIn(auth, email, password);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(null);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading('google');
    try {
      await initiateGoogleSignIn(auth);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(null);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading('apple');
    try {
      await initiateAppleSignIn(auth);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(null);
    }
  };

  const handleForgotPassword = async (): Promise<boolean> => {
    const resetEmail = email.trim();
    if (!resetEmail) {
      toast({
        title: t('common.error'),
        description: t('login.enterEmailForReset'),
        variant: 'destructive',
      });
      return false;
    }

    setLoading('forgot');
    auth.languageCode = i18n.resolvedLanguage?.split('-')[0] ?? null;
    try {
      await initiatePasswordReset(auth, resetEmail);
      toast({
        title: t('common.success'),
        description: t('login.passwordResetSent', { email: resetEmail }),
      });
      return true;
    } catch (err) {
      const errorKey = getPasswordResetErrorKey(err);

      if (!errorKey) {
        toast({
          title: t('common.success'),
          description: t('login.passwordResetSent', { email: resetEmail }),
        });
        return true;
      }

      toast({
        title: t('common.error'),
        description: t(errorKey),
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(null);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    tabIndex,
    setTabIndex,
    loading,
    handleSignUp,
    handleSignIn,
    handleGoogleSignIn,
    handleAppleSignIn,
    handleForgotPassword,
  };
}
